import { useState, useEffect, useRef } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const LUNAR_ZONES = [
  { id: 'base', label: '月球基地', icon: 'ri-building-4-line', color: '#00FFCC', desc: '南极永久阴影区，氦-3能源基地，可容纳5000人' },
  { id: 'mining', label: '矿产开采', icon: 'ri-hammer-line', color: '#FFD700', desc: '氦-3、钛、铝、稀土矿产，年产值超过5000亿' },
  { id: 'tourism', label: '月球旅游', icon: 'ri-map-pin-line', color: '#FF6B9D', desc: '月球酒店、低重力体验、地球观景台，年接待10万人' },
  { id: 'science', label: '科研中心', icon: 'ri-flask-line', color: '#A855F7', desc: '月球天文台、量子实验室、生命科学研究中心' },
  { id: 'transport', label: '太空港', icon: 'ri-flight-takeoff-line', color: '#00FF88', desc: '地月往返航班每日12班，月火中转站，太阳系门户' },
  { id: 'energy', label: '能源基地', icon: 'ri-flashlight-line', color: '#FF6B35', desc: '氦-3核聚变发电，向地球输送清洁能源，年产值万亿' },
];

const LUNAR_RESOURCES = [
  { name: '氦-3', icon: 'ri-bubble-chart-line', color: '#00FFCC', amount: '110万吨', value: '可供地球用电1万年', desc: '月球表面蕴藏约110万吨氦-3，是地球核聚变能源的终极解决方案。1吨氦-3可替代1500万吨煤炭，彻底解决人类能源危机。' },
  { name: '钛铁矿', icon: 'ri-settings-3-line', color: '#FFD700', amount: '数十亿吨', value: '太空建设核心材料', desc: '月球表面富含钛铁矿，是建造太空站、飞船和月球基地的核心材料。就地取材可将建设成本降低90%。' },
  { name: '水冰', icon: 'ri-drop-line', color: '#00FF88', amount: '6亿吨+', value: '生命维持与火箭燃料', desc: '月球南极永久阴影区蕴藏超过6亿吨水冰，可电解为氢氧火箭燃料，是深空探索的"加油站"。' },
  { name: '稀土元素', icon: 'ri-sparkling-line', color: '#FF6B9D', amount: '超地球储量', value: '高科技产业核心', desc: '月球稀土储量远超地球，是制造芯片、电池、XR设备的关键材料，月球采矿将彻底改变全球供应链。' },
  { name: '铝硅酸盐', icon: 'ri-building-line', color: '#A855F7', amount: '无限量', value: '3D打印建筑材料', desc: '月球表面的铝硅酸盐可直接用于3D打印建筑，无需从地球运输材料，是月球基地扩建的基础资源。' },
  { name: '太阳能', icon: 'ri-sun-line', color: '#FF6B35', amount: '无限清洁能源', value: '无大气层遮挡', desc: '月球无大气层，太阳能利用效率是地球的8倍。月球轨道太阳能电站可向地球无线传输电力，年产值超过10万亿。' },
];

const LUNAR_TOURS = [
  {
    name: '月球观光7日游',
    price: '¥280万',
    duration: '7天',
    color: '#00FFCC',
    icon: 'ri-camera-line',
    highlights: ['地球升起观景台', '阿波罗遗址参观', '低重力体验馆', '月球车驾驶体验'],
    img: 'https://public.readdy.ai/ai/img_res/656a992d4df8b7158e5711be87c8b9fb.jpg',
  },
  {
    name: '月球矿产探险14日',
    price: '¥580万',
    duration: '14天',
    color: '#FFD700',
    icon: 'ri-hammer-line',
    highlights: ['氦-3采矿体验', '月球南极探险', '矿产实验室参观', '太空行走体验'],
    img: 'https://readdy.ai/api/search-image?query=moon%20mining%20operation%20helium-3%20extraction%20lunar%20surface%20astronauts%20equipment%20golden%20neon%20glow%20photorealistic%20cinematic%208k%20futuristic&width=600&height=400&seq=lunar-tour-2-v2&orientation=landscape',
  },
  {
    name: '月球科研驻留30日',
    price: '¥1200万',
    duration: '30天',
    color: '#A855F7',
    icon: 'ri-flask-line',
    highlights: ['月球天文台观测', '量子实验室工作', '生命科学研究', '月球基地生活体验'],
    img: 'https://readdy.ai/api/search-image?query=lunar%20research%20station%20observatory%20telescope%20moon%20surface%20scientists%20working%20purple%20neon%20glow%20photorealistic%20cinematic%208k%20futuristic&width=600&height=400&seq=lunar-tour-3-v2&orientation=landscape',
  },
];

const LUNAR_LIFE_SCENES = [
  { time: '月球清晨', icon: 'ri-sun-line', color: '#FFD700', scene: '月球基地的"清晨"由人工光系统模拟。居民在1/6重力下醒来，感觉身体轻盈如羽毛。早餐是水培蔬菜和3D打印食品，营养完整。透过基地的透明穹顶，地球正悬挂在漆黑的天空中，蓝色的光芒让人心旷神怡。' },
  { time: '月球工作', icon: 'ri-briefcase-line', color: '#00FFCC', scene: '月球工程师张伟驾驶月球车前往氦-3采矿区。在1/6重力下，月球车可以轻松跨越巨大的陨石坑。采矿机器人已经工作了整整一个月球日（约14个地球日），收集了价值数十亿的氦-3。' },
  { time: '月球娱乐', icon: 'ri-gamepad-line', color: '#FF6B9D', scene: '下班后，居民们来到月球体育馆。在1/6重力下，篮球可以投出30米高的弧线，跳高世界纪录在这里毫无意义。最受欢迎的是"月球飞行"——穿上特制翅膀，在低重力下真正飞翔。' },
  { time: '月球夜晚', icon: 'ri-moon-line', color: '#A855F7', scene: '月球的夜晚长达14个地球日。居民们在元宇宙中度过漫长的夜晚，与地球的家人实时视频通话，延迟仅1.3秒。有人在虚拟现实中重温地球的雨天，有人在月球天文台观测遥远的星系。' },
];

export default function LunarExploration() {
  const [activeZone, setActiveZone] = useState('base');
  const [activeResource, setActiveResource] = useState(0);
  const [activeTour, setActiveTour] = useState(0);
  const [activeScene, setActiveScene] = useState(0);
  const [animFrame, setAnimFrame] = useState(0);
  const animRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sceneRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.05 });

  useEffect(() => {
    animRef.current = setInterval(() => setAnimFrame(f => (f + 1) % 360), 50);
    sceneRef.current = setInterval(() => setActiveScene(s => (s + 1) % LUNAR_LIFE_SCENES.length), 5000);
    return () => {
      if (animRef.current) clearInterval(animRef.current);
      if (sceneRef.current) clearInterval(sceneRef.current);
    };
  }, []);

  const currentZone = LUNAR_ZONES.find(z => z.id === activeZone) || LUNAR_ZONES[0];

  return (
    <section id="lunar-exploration" className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(180deg, #060810 0%, #0a0c18 50%, #060810 100%)' }}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=moon%20surface%20lunar%20base%20colony%20futuristic%20dome%20structures%20earth%20visible%20in%20sky%20dark%20space%20background%20cinematic%20ultra%20detailed%208k%20concept%20art&width=1920&height=1080&seq=lunar-bg-v2&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-8"
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(0,255,204,0.06) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 opacity-8" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,204,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,204,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-800 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 mb-6 text-sm font-bold rounded-full" style={{ background: 'rgba(0,255,204,0.12)', border: '1.5px solid rgba(0,255,204,0.4)', color: '#00FFCC' }}>
            <i className="ri-moon-line" />
            月球探索 · 2040年全景 · 人类第一个地外家园
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FFCC] via-[#FFD700] to-[#A855F7]">月球探索</span>
            <span className="block text-2xl md:text-3xl mt-3 text-white/80">矿产·旅游·生活·能源 · 2040年全景</span>
          </h2>
          <p className="text-white/50 text-base max-w-3xl mx-auto leading-relaxed">
            月球不只是一块石头——它是人类文明的跳板、能源的宝库、太空旅游的第一站。
            2040年，月球基地将成为地球人的"第二故乡"，开启人类多星球文明的新纪元。
          </p>
        </div>

        {/* Main Grid: Map + Zones */}
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={`grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16 transition-all duration-800 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Lunar Visual */}
          <div className="lg:col-span-2">
            <div className="relative w-full rounded-3xl overflow-hidden" style={{ aspectRatio: '16/9', background: 'linear-gradient(135deg, rgba(0,255,204,0.06), rgba(10,12,24,0.95))', border: '2px solid rgba(0,255,204,0.2)' }}>
              <img
                src="https://readdy.ai/api/search-image?query=lunar%20base%20interior%20futuristic%20dome%20colony%20moon%20surface%20earth%20view%20teal%20cyan%20neon%20glow%20photorealistic%20cinematic%208k%20ultra%20detailed%20concept%20art&width=1200&height=675&seq=lunar-interior-v2&orientation=landscape"
                alt="月球基地内景"
                className="absolute inset-0 w-full h-full object-cover opacity-50"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(6,8,16,0.3) 0%, rgba(6,8,16,0.1) 50%, rgba(6,8,16,0.7) 100%)' }} />

              {/* Animated moon orbit */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 56.25" preserveAspectRatio="none">
                <ellipse cx="50" cy="28" rx="35" ry="20" stroke="rgba(0,255,204,0.15)" strokeWidth="0.3" fill="none" strokeDasharray="2,3" />
                <circle r="1.5" fill="#00FFCC" opacity="0.9">
                  <animateMotion dur="10s" repeatCount="indefinite" path="M15,28 A35,20 0 1,1 85,28 A35,20 0 1,1 15,28" />
                </circle>
                <circle r="1" fill="#FFD700" opacity="0.7">
                  <animateMotion dur="15s" repeatCount="indefinite" path="M85,28 A35,20 0 1,1 15,28 A35,20 0 1,1 85,28" />
                </circle>
              </svg>

              {/* Zone hotspots */}
              {[
                { id: 'base', x: 30, y: 60, icon: '🏗️' },
                { id: 'mining', x: 70, y: 40, icon: '⛏️' },
                { id: 'tourism', x: 50, y: 25, icon: '🌍' },
                { id: 'science', x: 20, y: 35, icon: '🔭' },
                { id: 'transport', x: 80, y: 65, icon: '🚀' },
                { id: 'energy', x: 60, y: 70, icon: '⚡' },
              ].map(spot => {
                const zone = LUNAR_ZONES.find(z => z.id === spot.id)!;
                return (
                  <button
                    key={spot.id}
                    className="absolute cursor-pointer transition-all duration-300 hover:scale-125"
                    style={{ left: `${spot.x}%`, top: `${spot.y}%`, transform: 'translate(-50%, -50%)' }}
                    onClick={() => setActiveZone(spot.id)}
                  >
                    <div className="w-10 h-10 flex items-center justify-center text-lg rounded-full" style={{ background: `${zone.color}25`, border: `2px solid ${zone.color}70`, boxShadow: `0 0 16px ${zone.color}50` }}>
                      {spot.icon}
                    </div>
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-0.5 text-xs whitespace-nowrap rounded-md" style={{ background: 'rgba(0,0,0,0.8)', border: `1px solid ${zone.color}40`, color: zone.color }}>
                      {zone.label}
                    </div>
                  </button>
                );
              })}

              {/* Moon globe animation */}
              <div className="absolute top-4 right-4 w-20 h-20">
                <div className="w-full h-full rounded-full relative" style={{ background: 'radial-gradient(ellipse at 35% 35%, #e8e8e8, #888, #444)', boxShadow: '0 0 30px rgba(0,255,204,0.3)' }}>
                  {[0,1,2].map(i => (
                    <div key={i} className="absolute rounded-full border" style={{ inset: `${i*6}px`, borderColor: `rgba(0,255,204,${0.25-i*0.06})`, animation: `spin ${6+i*3}s linear infinite`, animationDirection: i%2===0?'normal':'reverse' }} />
                  ))}
                  {[0,1,2,3].map(i => (
                    <div key={i} className="absolute w-1.5 h-1.5 rounded-full" style={{ background: '#00FFCC', top: `${50+42*Math.sin((i*Math.PI*2)/4+animFrame*Math.PI/180)}%`, left: `${50+42*Math.cos((i*Math.PI*2)/4+animFrame*Math.PI/180)}%`, transform: 'translate(-50%,-50%)', boxShadow: '0 0 4px #00FFCC' }} />
                  ))}
                </div>
              </div>

              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#00FFCC] animate-pulse" />
                  <span className="text-white/60 text-xs">点击图标探索月球各区域</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-white/40 text-xs">基地人口: 5000人</span>
                  <span className="text-white/40 text-xs">建立时间: 2038年</span>
                </div>
              </div>
            </div>

            {/* Active zone detail */}
            <div className="mt-4 p-5 rounded-2xl transition-all duration-300" style={{ background: `${currentZone.color}08`, border: `1.5px solid ${currentZone.color}35` }}>
              <div className="flex items-center gap-3 mb-2">
                <i className={`${currentZone.icon} text-xl`} style={{ color: currentZone.color }} />
                <h4 className="text-white font-bold text-base" style={{ color: currentZone.color }}>{currentZone.label}</h4>
              </div>
              <p className="text-white/65 text-sm leading-relaxed">{currentZone.desc}</p>
            </div>
          </div>

          {/* Right: Zone list + Life scene */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              {LUNAR_ZONES.map(zone => (
                <button
                  key={zone.id}
                  onClick={() => setActiveZone(zone.id)}
                  className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-300 text-left"
                  style={{ background: activeZone === zone.id ? `${zone.color}12` : 'rgba(255,255,255,0.02)', border: `1.5px solid ${activeZone === zone.id ? zone.color+'50' : 'rgba(255,255,255,0.08)'}`, transform: activeZone === zone.id ? 'translateX(4px)' : 'none' }}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full" style={{ background: `${zone.color}15` }}>
                    <i className={`${zone.icon} text-sm`} style={{ color: zone.color }} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">{zone.label}</p>
                    <p className="text-white/40 text-xs leading-tight line-clamp-1">{zone.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Life scene auto-rotate */}
            <div className="p-4 rounded-2xl flex-1" style={{ background: `${LUNAR_LIFE_SCENES[activeScene].color}08`, border: `1.5px solid ${LUNAR_LIFE_SCENES[activeScene].color}30` }}>
              <div className="flex items-center gap-2 mb-3">
                <i className={`${LUNAR_LIFE_SCENES[activeScene].icon} text-lg`} style={{ color: LUNAR_LIFE_SCENES[activeScene].color }} />
                <span className="font-bold text-sm" style={{ color: LUNAR_LIFE_SCENES[activeScene].color }}>{LUNAR_LIFE_SCENES[activeScene].time}</span>
              </div>
              <p className="text-white/60 text-xs leading-relaxed">{LUNAR_LIFE_SCENES[activeScene].scene}</p>
              <div className="flex gap-1 mt-3">
                {LUNAR_LIFE_SCENES.map((_, i) => (
                  <button key={i} onClick={() => setActiveScene(i)} className="h-1 rounded-full transition-all cursor-pointer" style={{ width: activeScene === i ? '24px' : '8px', background: activeScene === i ? LUNAR_LIFE_SCENES[i].color : 'rgba(255,255,255,0.2)' }} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Resources Section */}
        <div className="mb-16">
          <h3 className="text-white font-black text-2xl mb-8 text-center" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span style={{ color: '#FFD700' }}>月球资源</span> · 人类文明的能源宝库
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LUNAR_RESOURCES.map((res, i) => (
              <div
                key={res.name}
                className="p-6 rounded-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1"
                style={{ background: activeResource === i ? `${res.color}10` : 'rgba(255,255,255,0.02)', border: `1.5px solid ${activeResource === i ? res.color+'50' : 'rgba(255,255,255,0.08)'}` }}
                onClick={() => setActiveResource(i)}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-2xl" style={{ background: `${res.color}20`, border: `1px solid ${res.color}30` }}>
                    <i className={`${res.icon} text-xl`} style={{ color: res.color }} />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-base">{res.name}</h4>
                    <p className="text-xs font-bold" style={{ color: res.color }}>{res.amount}</p>
                  </div>
                </div>
                <p className="text-xs font-semibold mb-2" style={{ color: res.color }}>{res.value}</p>
                <p className="text-white/50 text-xs leading-relaxed">{res.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tourism Section */}
        <div className="mb-16">
          <h3 className="text-white font-black text-2xl mb-8 text-center" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span style={{ color: '#FF6B9D' }}>月球旅游</span> · 人生必打卡的终极体验
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {LUNAR_TOURS.map((tour, i) => (
              <div
                key={tour.name}
                className="rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-2"
                style={{ border: `1.5px solid ${activeTour === i ? tour.color+'60' : 'rgba(255,255,255,0.1)'}` }}
                onClick={() => setActiveTour(i)}
              >
                <div className="relative" style={{ height: '180px' }}>
                  <img src={tour.img} alt={tour.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: `${tour.color}25`, border: `1px solid ${tour.color}50`, color: tour.color }}>
                    <i className={`${tour.icon} mr-1`} />{tour.duration}
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <p className="text-white font-bold text-sm">{tour.name}</p>
                    <p className="font-black text-lg" style={{ color: tour.color, fontFamily: 'Orbitron, sans-serif' }}>{tour.price}</p>
                  </div>
                </div>
                <div className="p-4" style={{ background: activeTour === i ? `${tour.color}08` : 'rgba(255,255,255,0.02)' }}>
                  <div className="flex flex-col gap-1.5">
                    {tour.highlights.map(h => (
                      <div key={h} className="flex items-center gap-2 text-xs text-white/60">
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: tour.color }} />
                        {h}
                      </div>
                    ))}
                  </div>
                  <button className="mt-4 w-full py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap cursor-pointer" style={{ background: `${tour.color}20`, border: `1px solid ${tour.color}40`, color: tour.color }}>
                    立即预订 →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Banner */}
        <div className="rounded-3xl p-8 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(0,255,204,0.06), rgba(168,85,247,0.06))', border: '2px solid rgba(0,255,204,0.2)' }}>
          <h3 className="text-white font-black text-2xl mb-8 text-center" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            月球基地 · <span style={{ color: '#00FFCC' }}>2040年关键数据</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { label: '基地人口', value: '5,000人', color: '#00FFCC', icon: 'ri-user-line' },
              { label: '氦-3年产量', value: '500吨', color: '#FFD700', icon: 'ri-bubble-chart-line' },
              { label: '旅游年接待', value: '10万人次', color: '#FF6B9D', icon: 'ri-map-pin-line' },
              { label: '地月航班', value: '12班/日', color: '#00FF88', icon: 'ri-flight-takeoff-line' },
              { label: '能源产值', value: '万亿/年', color: '#A855F7', icon: 'ri-flashlight-line' },
              { label: '建立时间', value: '2038年', color: '#FF6B35', icon: 'ri-calendar-line' },
            ].map(stat => (
              <div key={stat.label} className="text-center p-4 rounded-2xl" style={{ background: `${stat.color}10`, border: `1px solid ${stat.color}25` }}>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-3" style={{ background: `${stat.color}20` }}>
                  <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
                </div>
                <p className="font-black text-base" style={{ color: stat.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</p>
                <p className="text-white/40 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
}
